/* Vault, Resources, note detail, holding editor
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 429-574.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

/* ⚠ `obsState`: HIS RULING 2026-10-03 (the fourth session) — an Obsolete
 * note's Resources row is TITLED with its state, the way its vault card
 * is; those rows carry no Fr number, so the title was empty. */
import { CATALOG, CUSTOM_GRADE, GLABEL, GRADES, customGradeNumber, gradeLabel, gradesOf, isCol, isCustomGrade, isObs, obsState, refOf } from './catalog.js'
import { cardSub, cardTitle, isErr, typeLabel } from './community.js'
import { OTHER_TYPE, isCust, isOther, sectionList } from './custom.js'
import { SORTLABEL, denomValue, frNum } from './filters.js'
import { FILTER_DEFS, applyFilters, detailLine, filterNote, rowDesc, yearOf } from './note-filters.js'
import { searchMatch } from './note-search.js'
import { holdChip } from './photos.js'
import { qualifierOn } from './memo.js'
import { CARD, DETAIL_ORDER, TYPE_LEAD, OTHER_LEAD,DIFFERENT_KEEP, DIFFERENT_LINE, DIFFERENT_SEE, DIFFERENT_UPDATE, PHOTO_FIRST_LINE, RAW_DIFFERENT_LINE, RAW_FILL_FIELD, UNCHECKED_DISMISS, YEAR_FILL_FIELD, UNCHECKED_LINE, getDifferent, getLookup, getPending, getReading, getUnchecked, lookupLine } from './pending.js'
import { pricedAt } from './suggest.js'
import { FRB_DISTRICTS, TYPE_OPTIONS, candidateText, districtOf, indexOf, isHidden, narrowCard, orderCandidates, sharesSection, varietyOf } from './note-narrow.js'
import { chip } from './render.js'
import { $, bookVal, cat, estVal, money, state, totals } from './state.js'

/* The "Other" type's two sentences (his ruling 1 of 2026-10-26; the words
 * are the builder's, his to judge on screen): the value field's hint, and
 * the card's word in place of "no price" until a value is entered. */
const OTHER_VALUE_HINT="Other notes have no catalog price — enter your value.";
const ADD_YOUR_VALUE="add your value";

function vCollection(){
  const t=totals();
  /* ⚠ THE READ FAILED — the offline-reload item, his go 2026-10-18, his words exactly: this panel in place of the
   * empty state, so a vault this device could not read never says "Your vault is empty" or $0. `bootSession` sets
   * the flag; `main.js`'s [data-vaultretry] runs the boot again. `search-check` G1–G5; `search-sabotage` SB79–SB84. */
  if(state.vaultUnread){
    return `<div class="empty vault-unread"><div class="ic"><span>◈</span></div>
      <h3>Your vault didn't load</h3>
      <p>This device couldn't load it just now — your notes are safe on your account, so try again in a moment.</p>
      <button class="btn sm" data-vaultretry style="margin:0 auto"${state.vaultRetrying?' disabled':''}>${state.vaultRetrying?'Trying…':'Try again'}</button></div>`;
  }
  if(!state.collection.length){
    return `<div class="empty"><div class="ic"><span>◈</span></div>
      <h3>Your vault is empty</h3>
      <p>Add a note from Resources to start tracking what it's worth and what you paid.</p>
      <button class="btn sm" data-nav="catalog" style="margin:0 auto">Browse Resources</button></div>`;
  }
  const glcls=t.g>=0?"pos":"neg";const pct=t.c?((t.g/t.c)*100):0;
  /* The unpriced count — Phase 6.5 step 3.4, his ruling 2026-09-15: "The
   * total counts what it can price; the unpriced count beside it is how a
   * collector sees the rest. No silent disappearance." totals() has computed
   * `priced` since Phase 1 and nothing rendered it. The count is every note
   * estVal() cannot price — a custom grade, a hand-entered note, or a catalog
   * row with no prices — none of them appraised. Concatenated, not template
   * literals, so the twin's anchors carry no `${` and anchor-sweep checks them. */
  const unpriced=state.collection.length-t.priced;
  const lbl=unpriced>0?'Estimated value of priced notes':'Estimated collection value';
  const unpricedLine=unpriced>0?'<div class="unpriced">'+unpriced+' of '+state.collection.length+' unpriced · not in the total</div>':'';
  const q=state.vSearch.trim().toLowerCase();
  let list=state.collection.map(h=>({h,n:cat(h.catId)})).filter(o=>o.n);
  /* ⚠⚠ ALL FIVE FILTERS THROUGH ONE CALL. Written out, each new filter
   * is another line that can be forgotten in one of the two views —
   * `applyFilters` is the same code the profile runs and the same code
   * `filter-check` proves. ⚠ "All" keeps a note that carries nothing. */
  /* ⚠ Other's step 6: a custom note filters by its own year (`filterNote`, his ruling 7 and his A) */
  list=applyFilters(list,def=>state[def.v],o=>filterNote(o.n,o.h.year));
  /* ⚠⚠ STOP C (C3), 2026-10-03: ONE haystack for the vault, the profile
   * and Resources (`note-search.js`) — the new fields, the year from
   * `yearOf` (search and sort agreed about nothing before: Fr 466 sorted as
   * 1882 and searched as no-year), the WWII words, and his exact-place
   * rule. The vault still passes what only it knows: the collector's own
   * reference, year and grade. */
  if(q)list=list.filter(o=>searchMatch(q,o.n,{ref:refOf(o.h,o.n),year:o.h.year,grade:gradeLabel(o.h.grade),labelOf:typeLabel},CATALOG));
  /* ⚠⚠ THE YEAR NOW COMES FROM `yearOf` — his ruling 2026-09-20. The
   * collector's own year still wins; a RECOVERED `series` year is used
   * where it exists; `desc` is the fallback it always was. */
  const so=state.vSort;const vy=o=>(yearOf(o.h.year,o.n)||0);
  if(so==="fr_asc")list.sort((a,b)=>frNum(a.n.fr)-frNum(b.n.fr)||(a.n.fr>b.n.fr?1:-1));
  else if(so==="fr_desc")list.sort((a,b)=>frNum(b.n.fr)-frNum(a.n.fr)||(a.n.fr<b.n.fr?1:-1));
  else if(so==="year_asc")list.sort((a,b)=>(vy(a)||9999)-(vy(b)||9999));
  else if(so==="year_desc")list.sort((a,b)=>(vy(b)||-1)-(vy(a)||-1));
  const cards=list.map(o=>{const h=o.h,n=o.n;
    const e=estVal(h);const gl=e!=null?e-(+h.cost||0):null;const glc=gl==null?"":gl>=0?"pos":"neg";
    /* One named local so the twin can anchor on it without interpolation (3.2, 3.7-walk). */
    const cardGrade=gradeLabel(h.grade);
    /* ⚠⚠ THE SAME PILL A STRANGER SEES — HIS INSTRUCTION, 2026-09-21:
     * *"If my vault cards don't show EPQ/PPQ at all, they should show the
     * same EPQ/PPQ pill strangers see — my own view shouldn't show less
     * than the public one."* They did not, and that asymmetry was mine:
     * I added the pill to `views-community.js` because db/012 made the
     * field PUBLIC, and never asked whether the OWNER'S list showed it.
     * **A field going public is not a reason for the owner to see less
     * of it.** ⚠ Same class as the `x.n ||` misses recorded at
     * `filters.js:28` — one renderer of a shape fixed and its twin left.
     *
     * ⚠ `qualifierOn(h)`, the compatibility read, NOT `h.qualifier`: a
     * card is the first thing drawn after boot, and on a device whose
     * carry-over has not run the letters are still in the memo. This and
     * the note screen must never disagree about one holding. */
    const cardQual=qualifierOn(h);
    /* his ruling of 2026-10-22: a different-note prompt that SET while the
     * collector was not on this note's screen marks its card, while the
     * prompt stands (Keep as is or Update takes both). The words are the
     * builder's, beyond his wording ("a small mark"). */
    const cardDif=getDifferent(state,h.id);
    const difMark=cardDif&&cardDif.mark?'<span class="pill diffmark" title="This photo looks like a different note">Photo may not match</span>':"";
    /* ⚠ THE OPEN NOTE'S CARD, IN B ONLY: this list is drawn on the `holding` screen only beside the note (render.js
     * `renderPanes`); on a phone it is drawn on `collection` alone, where `state.sel` survives Back — so the test is
     * the screen, never `sel` by itself, and no phone card ever carries the mark. */
    const openHere=state.screen==="holding"&&state.sel===h.id?' aria-current="true"':"";
    /* an Other note with no value asks for one (his ruling 1, 2026-10-26); `estVal` is null only while it has none */
    const noPxWord=isOther(n)?ADD_YOUR_VALUE:(n.st?"unpriced":"no price");
    return `<button class="card" data-hold="${h.id}"${openHere}>
      ${holdChip(h,n)}
      <div class="body">
        <div class="t1"><span class="fr">${escH(cardTitle(n,h.year))}</span></div>
        <div class="desc">${escH(cardSub(n,h.year))}</div>
        <div class="pills" style="margin-top:6px">${h.grade?`<span class="pill grade">${cardGrade}</span>`:""}${cardQual?`<span class="pill qual">EPQ/PPQ</span>`:""}${difMark}</div>
      </div>
      <div class="right">
        <div class="est">${money(e)}</div>
        ${gl!=null?`<div class="gl ${glc}">${gl>=0?"+":"−"}${money(Math.abs(gl)).slice(1)}</div>`:`<div class="gl" style="color:#5f7a68">${noPxWord}</div>`}
      </div></button>`;
  }).join("");
  const active=[];
  for(const def of FILTER_DEFS){const v=state[def.v];if(v&&v!=="All")active.push([def.key,def.key==="denom"?v+" notes":v]);}
  if(so!=="added")active.push(["sort",SORTLABEL[so]]);
  const filtered=active.length>0||q;
  const activeHtml=active.length?`<div class="activefilters">${active.map(a=>`<span class="afchip">${a[1]}<span class="x" data-rmfilter="${a[0]}">✕</span></span>`).join("")}<button class="afclear" data-clearfilters>Clear all</button></div>`:"";
  return `<div class="vault">
      <div class="lbl">${lbl}</div>
      <div class="big mono">${money(t.v)}</div>${unpricedLine}
      <div class="sub">
        <div><span class="k">Cost basis</span><span class="v mono">${money(t.c)}</span></div>
        <div><span class="k">Gain / loss</span><span class="v mono ${glcls}">${t.g>=0?"+":"−"}${money(Math.abs(t.g)).slice(1)}</span></div>
        <div><span class="k">Return</span><span class="v mono ${glcls}">${pct>=0?"+":""}${pct.toFixed(1)}%</span></div>
      </div>
    </div>
    <div class="rowhead"><h2>Your notes</h2><span class="count">${filtered?`${list.length} of ${state.collection.length}`:`${state.collection.length} held`}</span></div>
    <div class="vsearchrow">
      <div class="search"><span class="mag">⌕</span><input id="vq" placeholder="Search Fr#, type, series, state…" value="${escH(state.vSearch||"")}"></div>
      <button class="filterbtn ${active.length?"on":""}" data-openfilters="vault">${active.length?'<span class="dot"></span>':""}Filters</button>
    </div>
    ${activeHtml}
    ${cards||`<div class="empty" style="padding:30px 20px"><p>No notes match. Try clearing a filter or your search.</p><button class="btn sm" data-clearfilters style="margin:14px auto 0">Clear filters</button></div>`}
    <div class="hint">Values are Friedberg 23rd-ed. estimates for the grade you selected — not live market quotes.</div>`;
}

function denoms(){const d=[...new Set(CATALOG.filter(n=>!isCol(n)).map(n=>n.fd||n.denom))];
  return d.sort((a,b)=>denomValue(a)-denomValue(b));}
// If the query is a plain dollar denomination (e.g. "5000", "5,000", "$5,000"),
// return its numeric value so we can match notes by denomination — comma-agnostic,
// and without "1000" leaking into "10000" the way a substring match would.
function denomQueryVal(q){const t=(""+q).trim().replace(/[$,\s]/g,"");return (t&&/^\d+$/.test(t))?+t:null;}

function vCatalog(adding){
  const q=state.search.trim().toLowerCase();
  /* ⚠ HIS (8), 2026-09-29: the four Fractional rows the book says do not
   * exist stay in the catalogue, and *"catalogue search never shows them"*
   * — `isHidden` (note-narrow.js) is the one list; a saved note that points
   * to one still resolves through `cat()`. */
  let list=CATALOG.filter(n=>!isHidden(n));
  if(state.filter==="Error")list=list.filter(n=>isErr(n));
  else if(state.filter!=="All")list=list.filter(n=>(n.fd||n.denom)===state.filter);
  if(q){
    const qDen=denomQueryVal(q);
    list=list.filter(n=>{
      /* ⚠ STOP C (C3): the same haystack and exact-place rule as the vault */
      if(searchMatch(q,n,{labelOf:typeLabel},CATALOG))return true;
      if(qDen!=null&&denomValue(n.fd||n.denom)===qDen)return true;
      return false;
    });
  }
  const shown=list.slice(0,q?600:120);
  const fchips=["All","$1","$2","$5","$10","$20","$50","$100","Error"].map(f=>`<button class="fchip ${state.filter===f?"on":""}" data-filter="${f}">${f}</button>`).join("");
  const rows=shown.map(n=>{
    const top=n.g.find(x=>x!=null);
    return `<button class="card" data-note="${n.id}">
      ${chip(n)}
      <div class="body">
        <div class="t1"><span class="fr">${isObs(n)?obsState(n):isErr(n)||isCol(n)?n.fr:"Fr. "+n.fr}</span></div>
        <div class="desc">${isErr(n)?"Error Note":(rowDesc(n)||n.section)}</div>
      </div>
      <div class="right"><div class="est" style="font-size:13px;color:var(--sage)">${isErr(n)?"Error":n.denom}</div>
      <div class="gl" style="color:var(--gold)">${money(top)}+</div></div></button>`;
  }).join("");
  return `<div style="padding-top:6px"></div>
    ${adding?`<button class="back" data-back="collection" style="padding-top:6px">‹ Vault</button><div class="rowhead"><h2>Pick a note to add</h2><span class="count">search or filter to find it</span></div>`:""}
    <div class="search"><span class="mag">⌕</span><input id="q" placeholder="Search Fr#, type, series, state…" value="${escH(state.search)}"></div>
    <div class="filters">${fchips}</div>
    <div class="rowhead"><h2 style="font-size:14px">${state.filter==="All"?"All notes":state.filter+" notes"}</h2><span class="count">${list.length} match${shown.length<list.length?" · top "+shown.length:""}</span></div>
    ${rows||'<div class="empty"><p>No notes match that search.</p></div>'}`;
}

function vDetail(){
  const n=cat(state.sel);
  const rows=gradesOf(n).map((g,i)=>{const p=n.g[i];
    return `<div class="gr"><span class="g">${GLABEL[g]||g}</span><span class="p" style="color:${p==null?'#5f7a68':'var(--gold)'}">${money(p)}</span></div>`;}).join("");
  return `<button class="back" data-back="1">‹ Back to Resources</button>
    <div class="hero"><div class="fr">${isErr(n)||isCol(n)||isObs(n)?n.fr:"Fr. "+n.fr}</div><div class="dn">${isErr(n)?typeLabel(n.section):(isCol(n)?n.denom+" · Colonial":n.denom+" · "+typeLabel(n.section))}</div>${n.desc?`<div class="ds">${n.desc}</div>`:""}${detailLine(n)?`<div class="ds nline">${detailLine(n)}</div>`:""}${n.st?`<div class="ds" style="color:var(--gold)">${n.st}</div>`:""}</div>
    <div class="rowhead"><h2>Value by grade</h2></div>
    <div class="gtable">${rows}</div>
    <button class="btn" data-addfrom="${n.id}">Add this note to my vault</button>
    <div class="hint">You'll choose your note's grade and enter what you paid on the next step.</div>`;
}

/* ══ STEP 6 — THE READING CARD, "WHAT THE PHOTO SHOWS" — HIS RULING
 * 2026-09-26. The six read details as editable fields (✨ read, ✎ the
 * collector's correction, the placeholder on an unread one), and under
 * them the catalogue narrowed from those fields on EVERY render: the
 * narrower runs here, on the card's values, so an edit re-narrows live.
 * The four states in his wording: rows to tap (1–12; a single match is
 * still a row — the app never picks), the over-12 line with no rows (his
 * 6.5 rule), the nothing line, and the no-Fr line, which offers no rows
 * ever (rulings 3: edits still re-narrow). Concatenated, not a template
 * literal, so the twin's anchors carry no `${`. Escaped: the values are
 * a reader's or the collector's bytes.
 * ⚠ STOP B, HIS RULINGS 2026-09-27 — THE APPROVED THIRD-ROUND MOCKUPS.
 * What shows is `cardState`'s (note-narrow.js): Denomination, Type and
 * Series always; Charter period, Variety, Star note, State or colony and
 * Federal Reserve Bank by the type's table, and "Add another detail" when
 * the type is not known. Type, Charter period, Variety, Star and the Bank
 * are pick-lists that open on "not read — tap to add"; a split heading
 * with no year asks its question with two answers (the size, or the
 * Treasury issue); an unread field is dashed (his a). The list is
 * `narrowCard`'s — the call `pickCandidate` guards a tap with — ordered
 * (the read letter first, then red, blue, no seal), and a row says every
 * signature pair, the seal after them, and the rest (`candidateText`).
 * The no-Fr line is his F when every row is Obsolete. */
const escH=s=>String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
/* the Type select's lead option carries `TYPE_LEAD` (pending.js) */
const RD_LEAD=TYPE_LEAD;
const rdSelect=(f,cls,opts,val)=>'<select class="'+cls+'" data-rdf="'+f+'">'+opts.map(o=>'<option value="'+escH(o[0])+'"'+(o[0]===val?" selected":"")+'>'+escH(o[1])+'</option>').join("")+'</select>';
/* ⚠ HIS RULING 2 OF 2026-10-06: `onCat` — the card a CATALOGUE note's "See
 * likely notes" opened — prints the catalogue versions of the three lines
 * written for a custom note (pending.js `CARD.*Keep`). */
/* his ruling on Other's step 3 (2026-10-26): `oth` — "On an Other note, the
 * card's no-match line uses 'fix it above, or keep this note as it is'
 * instead of pointing at the Fr field" (step 1 hides that field on Other). */
function readingCard(rd,onCat,oth){
  const {card,res}=narrowCard(rd,CATALOG);const t=card.type;const f=rd.fields;
  const mk=k=>k==="typed"?'<span class="ai" title="'+CARD.typedMark+'" aria-label="'+CARD.typedMark+'">✎</span>':k==="read"?'<span class="ai" title="'+CARD.readMark+'" aria-label="'+CARD.readMark+'">✨</span>':"";
  /* what each pick-list holds; a text field holds its own value. The
   * amendment's QUESTION rows (his F, G, I, #8) hold the lead while asked,
   * else their answer. */
  const Q=card.questions||{};
  /* ⚠ OTHER ON THE CARD — problem 1 of his walk of `8ff48b00`, his rulings: a custom note's card offers Other after
   * the 22 types, and an Other note's card shows it — *"the card shows Other ✨ when the AI filled it"*, ✎ when it is
   * his (his #11). Never on a catalogue note's card; the reading and `TYPE_OPTIONS` never carry it (main.js). */
  const otc=oth&&!onCat;
  const val={type:otc?OTHER_TYPE:t.kind==="unread"?"":["ask","mixed","unknown"].includes(t.kind)?RD_LEAD:t.section,charter:card.charter.value||"",variety:varietyOf(f.variety)||"",star_serial:f.star_serial||"",district:districtOf(f.district)||""};
  for(const k in Q)val[k]=Q[k].asked?RD_LEAD:(Q[k].value||"");
  const has=d=>!!(d in val?val[d]:String(f[d]==null?"":f[d]).trim());
  /* ✨ read (or derived from the read year, or filled by his #9), ✎ the
   * collector's (his #2: a size decided by a year THEY typed is theirs; his
   * ruling 3 of 2026-10-02: a Charter period decided by a year they typed is
   * theirs the same way), nothing when empty or while a question waits */
  const whose=d=>!has(d)?"":d==="type"?(otc?(rd.noFr&&rd.noFr.section?"read":"typed"):(t.kind==="typed"||(t.kind==="derived"&&t.typedYear)?"typed":"read")):d==="charter"?(card.charter.derived&&!card.charter.typedYear?"read":"typed"):Q[d]?(Q[d].asked?"":Q[d].filled?"read":"typed"):rd.typed[d]?"typed":rd.read[d]?"read":"";
  const cls=d=>"rdf"+(whose(d)==="typed"?" typed":"")+(has(d)?"":" unread")+((d==="type"&&t.kind==="ask"&&!otc)||(Q[d]&&Q[d].asked)?" ask":"");
  const NR=["",CARD.notRead];
  const input=d=>{
    /* ⚠ HIS ANSWER ON THE SIZE QUESTION, 2026-10-29: *"yes — while the card shows Other, drop the size lead option and
     * the size buttons; picking a type from the list is the one way back."* — so no read heading's lead either */
    /* ⚠ A1, HIS RULING OF 2026-10-30: while the AI's fill stands, the selected option is `OTHER_LEAD` ("Other ✨ —
     * filled from your photo") and plain Other sits last, so a pick of Other is a value change (pending.js) */
    if(d==="type"&&otc&&rd.noFr&&rd.noFr.section)return rdSelect(d,cls(d),[[OTHER_LEAD,CARD.otherFilled],NR].concat(TYPE_OPTIONS.map(s=>[s,typeLabel(s)]),[[OTHER_TYPE,typeLabel(OTHER_TYPE)]]),OTHER_LEAD);
    if(d==="type"&&otc)return rdSelect(d,cls(d),[NR].concat(TYPE_OPTIONS.map(s=>[s,typeLabel(s)]),[[OTHER_TYPE,typeLabel(OTHER_TYPE)]]),val.type);
    if(d==="type"){
      const lead=t.kind==="ask"?[[RD_LEAD,t.split.ask==="issue"?CARD.askIssue:CARD.askSize(t.split.name)]]:["mixed","unknown"].includes(t.kind)?[[RD_LEAD,t.text]]:[];
      return rdSelect(d,cls(d),lead.concat([NR],TYPE_OPTIONS.map(s=>[s,typeLabel(s)]),onCat?[]:[[OTHER_TYPE,typeLabel(OTHER_TYPE)]]),val.type)
        +(t.kind==="ask"?'<div class="sizeask">'+t.split.options.map((s,i)=>'<button class="szb" data-rdtype="'+escH(s)+'">'+(t.split.ask==="issue"?(i?CARD.issueNew:CARD.issueOld):(i?CARD.small:CARD.large))+'</button>').join("")+'</div>':"");
    }
    /* a QUESTION row: the question as the lead, the answers the list
     * offers as its options, and the answers as buttons while it waits
     * (the approved question shape, the Type row's) */
    if(Q[d]){const q=Q[d];
      return rdSelect(d,cls(d),[[RD_LEAD,CARD.questionText(d,q.answers)]].concat(q.answers.map(v=>[v,CARD.answerText(d,v)])),val[d])
        +(q.asked?'<div class="sizeask">'+q.answers.map(v=>'<button class="szb" data-rdans="'+escH(d)+'" data-rdv="'+escH(v)+'">'+escH(CARD.answerText(d,v))+'</button>').join("")+'</div>':"");
    }
    if(d==="charter")return rdSelect(d,cls(d),[NR].concat(["First","Second","Third"].map(v=>[v,CARD.charterOption(v)])),val.charter);
    if(d==="variety")return rdSelect(d,cls(d),[NR].concat(["Type 1","Type 2"].map(v=>[v,CARD.varietyOption[v]])),val.variety);
    if(d==="star_serial")return rdSelect(d,cls(d),[NR,["yes",CARD.starYes],["no",CARD.starNo]],val.star_serial);
    if(d==="district")return rdSelect(d,cls(d),[NR].concat(FRB_DISTRICTS.map(x=>[x,x])),val.district);
    return '<input class="'+cls(d)+'" data-rdf="'+d+'" placeholder="'+CARD.notRead+'" value="'+escH(f[d]||"")+'" maxlength="40">';
  };
  const fields=DETAIL_ORDER.filter(d=>d==="denomination"||d==="type"||d==="series"||card.shown.includes(d))
    .map(d=>'<div class="field rf"><label>'+(Q[d]?CARD.questionLabel(d,Q[d].answers):CARD.labels[d])+mk(whose(d))+'</label>'+input(d)+'</div>').join("")
    +(card.hidden.length?'<button class="addmore" data-rdmore="1">'+CARD.addMore+'</button>':"");
  let list;
  if(res.state==="no-fr")list='<div class="whyrow rdline" data-rdstate="no-fr">'+(res.candidates.every(c=>c.section==="Obsolete Note")?(onCat?CARD.noFrObsoleteKeep:CARD.noFrObsolete):(onCat?CARD.noFrKeep:CARD.noFr))+'</div>';
  else if(res.state==="nothing")list='<div class="whyrow rdline" data-rdstate="nothing">'+(onCat||oth?CARD.nothingKeep:CARD.nothing)+'</div>';
  else if(res.state==="did-not-narrow")list='<div class="whyrow rdline" data-rdstate="did-not-narrow">'+CARD.tooMany(res.count)+'</div>';
  else{
    /* his C: `res.net` is the safety net — over the bar, nothing left to
     * narrow, the whole list shown; the head says so for the gate */
    const drop=sharesSection(res.candidates);
    list='<div class="rowhead rdhead" data-rdstate="narrowed"'+(res.net?' data-rdnet="1"':"")+'><h2>'+(res.count===1?CARD.matchesOne:CARD.matchesN(res.count))+'</h2><span class="count">'+CARD.tapYours+'</span></div>'
      +orderCandidates(res.candidates,f).map(c=>{const x=candidateText(c,{dropSection:drop,labelOf:typeLabel});
        return '<button class="card cand" data-pickrow="'+escH(c.sid)+'"><div class="body"><div class="t1"><span class="fr">Fr. '+escH(c.fr)+'</span><span class="pill grade">'+escH(c.denom)+'</span></div>'
          +(x.sigs.length?'<div class="sigs">'+x.sigs.map(escH).join('<span class="sep"> · </span>')+'</div>':"")
          +(x.rest?'<div class="desc rest">'+escH(x.rest)+'</div>':"")
          +'</div><div class="right"><span class="pick">'+CARD.pick+'</span></div></button>';}).join("");
  }
  /* ⚠ HIS RULING (c), 2026-10-29: *"c. Because if anything a collector can always manually change the type if AI got it
   * wrong."* — on an Other note, his or the AI's, no rows and no other line: only the keep one. `other` is its own state
   * (the builder's, flagged) so it never reads as "nothing matched"; the narrowing itself is untouched (suggest.js). */
  if(otc)list='<div class="whyrow rdline" data-rdstate="other">'+CARD.nothingKeep+'</div>';
  return '<div class="acard rdcard"><div class="acard-h">'+CARD.title+'</div><div class="fieldhint intro">'+CARD.intro+'</div>'+fields+'</div>'+list;
}

function vHolding(){
  const h=state.collection.find(x=>x.id===state.sel);const n=cat(h.catId);
  const nopx=(n.g||[]).every(v=>v==null);
  /* ── STEP 4.3, HIS RULING 2026-09-21 (SECOND) ────────────────────────
   * The AI FILLS the field and a ✨ says it did. There is no confirm
   * step: the value is the collector's the moment it lands, and the
   * marker is what makes it honest. ⚠ `aiG` is a MARKER, not a pending
   * value — the grade below is already `h.grade`. */
  const aiG=getPending(state,h.id,"grade");
  const aiQ=getPending(state,h.id,"qualifier");
  const star='<span class="ai" aria-label="Filled from your photo" title="Filled from your photo">\u2728</span>';
  /* \u2500\u2500 STEP 7.3(b), 2026-09-23 \u2014 THE IDENTITY \u2728 AND ITS BUTTON, PIXELS
   * ONLY; THE HANDLERS ARE NOT WIRED (his order: pixels first).
   * `catId` is the field the slab lookup changes, so its marker sits on
   * the hero's Fr line \u2014 after a confident match the note IS a catalogue
   * row and the Note-details card with `#cfr` is gone, so there is no
   * field left to mark. \u26a0 THE BUTTON FOLLOWS THE MARKER EXACTLY \u2014 his
   * confirmation 2026-09-23: it exists while the identity \u2728 exists and
   * nowhere else, so a catalogue-picked note, a typed conversion, and a
   * note after Save or leaving never show it. `data-notright` matches no
   * handler today; (c) wires it and the marker's writer. A marker on a
   * CUSTOM note is impossible by construction (the lookup marks only a
   * conversion) and is not rendered if it ever appears. */
  const aiId=!isCust(n)&&getPending(state,h.id,"catId");
  const idStar=aiId?'<span class="ai" aria-label="Identified from your photo" title="Identified from your photo">\u2728</span>':"";
  /* ⚠ STEP 7.5: after "Update this note" the ✨ shows (his ruling, "matching
   * a first identification") but the button does not — it reverts to a
   * blank CUSTOM note, and the note was a catalogue row before Update. */
  const notRight=aiId&&aiId.source!=="different"?'<button class="btn ghost notright" data-notright="'+h.id+'">Not the right note? Tap here.</button>':"";
  /* The refusal line \u2014 one plain sentence under the Fr field, never
   * silence, and it says which refusal (pending.js carries the four).
   * It replaces the field's own hint while it shows; when the collector
   * types, their act supersedes it and the hint takes over ((c) clears
   * it). \u26a0 `fr` came from the reader, so the whole line is ESCAPED. */
  const esc=s=>String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  /* ── STEP 7.5, HIS RULING 2026-09-25 — "this photo looks like a different
   * note". A catalogue note only (pending.js carries the rule and his
   * wording). Under the hero, where "Not the right note?" sits; the two
   * never show together, because that button needs a CUSTOM-to-row match
   * and this needs a catalogue note whose photo is another row. */
  /* ⚠ HIS RULINGS OF 2026-10-06: a RAW prompt (`dif.raw`, no row) reads his
   * denomination line with "See likely notes" — gone once tapped, the card
   * under it then — and "Keep as is"; and 7.5's line names a note with no Fr
   * by its vault card's title (`cardTitle`), never "Fr. " with nothing after. */
  const dif=!isCust(n)?getDifferent(state,h.id):null;const difRow=dif&&!dif.raw?cat(dif.sid):null;
  const difBox=difRow?'<div class="diffnote"><div class="whyrow">'+esc(DIFFERENT_LINE(difRow,n.fr,cardTitle(n,h.year)))+'</div><div class="diffbtns"><button class="btn" data-diffupdate="'+h.id+'">'+DIFFERENT_UPDATE+'</button><button class="btn ghost" data-diffkeep="'+h.id+'">'+DIFFERENT_KEEP+'</button></div></div>'
    :dif&&dif.raw?'<div class="diffnote rawdiff"><div class="whyrow">'+esc(RAW_DIFFERENT_LINE(dif.read.fields.denomination,n.denom))+'</div><div class="diffbtns">'+(dif.open?'':'<button class="btn" data-diffsee="'+h.id+'">'+DIFFERENT_SEE+'</button>')+'<button class="btn ghost" data-diffkeep="'+h.id+'">'+DIFFERENT_KEEP+'</button></div></div>':"";
  /* ── HIS RULING OF 2026-10-22: a REPLACED front the app did not check — one
   * plain line under the hero with Dismiss (pending.js carries the rule and
   * his text). Never beside a prompt: a prompt means the photo WAS checked. */
  const unc=getUnchecked(state,h.id);
  const uncBox=unc&&!dif?'<div class="unchecked"><div class="whyrow">'+esc(UNCHECKED_LINE)+'</div><div class="diffbtns"><button class="btn ghost" data-uncheckdismiss="'+h.id+'">'+UNCHECKED_DISMISS+'</button></div></div>':"";
  /* ── STEP 6: the card, on a CUSTOM note with no typed Fr, where the
   * lookup line sits; ~~a raw reading on a catalogue note or~~ a typed-Fr
   * note never reaches `state.reading` (suggest.js) — a catalogue note's
   * does since 2026-10-06, through "See likely notes" alone. The Denomination
   * field's ✨ is his ruling (2). */
  /* ⚠ 2026-10-06: and on a CATALOGUE note once "See likely notes" opened it
   * (his ruling 3) — drawn under the prompt, above the photos (the catalogue
   * order, `rdBox+photos`). Only while the prompt stands: "Keep as is", a
   * row tap, Save and leaving all take the prompt, and the card with it. */
  const rd=isCust(n)?(!n.fr?getReading(state,h.id):null):(dif&&dif.raw&&dif.open?getReading(state,h.id):null);
  const rdBox=rd?readingCard(rd,!isCust(n),isOther(n)):"";
  const aiD=isCust(n)&&getPending(state,h.id,RAW_FILL_FIELD);
  /* his ruling 2 of 2026-10-26: the year filled from the photo's series carries the ✨ while the marker stands */
  const aiY=isCust(n)&&getPending(state,h.id,YEAR_FILL_FIELD);
  /* ── STOP B, HIS RULING 3 (2026-09-27): the no-Fr answer over one type
   * fills the entry's Type and its state (suggest.js `syncNoFrFill`); the
   * ✨ shows while the card's mark stands. Note details gains State or
   * colony on a custom note whose type carries one (the catalogue's own
   * facts: Colonial and Obsolete) or that already holds one. The value is
   * the entry's `desc` — the reader's or the collector's bytes, so it is
   * ESCAPED here and in the hero (his catch, 2026-09-27).
   * ⚠ SINCE HIS RULING OF 2026-10-05 `aiT` marks the Type fill on EVERY
   * reading whose card names one Type, not only on a no-Fr answer — the same
   * mark (`syncNoFrFill`, suggest.js). */
  const aiT=!!(rd&&rd.noFr&&rd.noFr.section),aiS=!!(rd&&rd.noFr&&rd.noFr.desc);
  const stF=isCust(n)&&(!!String(n.desc||"").trim()||!!(indexOf(CATALOG).facts.get(n.section)||{}).state);
  const lk=isCust(n)?getLookup(state,h.id):null;
  const lkText=lk?lookupLine(lk.reason,lk.fr,lk):null;
  const frHint=lkText?'<div class="whyrow lookupline">'+esc(lkText)+'</div>':'<div class="fieldhint">Enter a catalog Fr. number and Currency Keep will link it automatically, bringing in catalog values.</div>';
  /* ⚠ HIS #12, 2026-09-28: *"Hide the Fr. number field in Note details
   * when Type is Obsolete Note."* An Obsolete note has none (the Reference
   * no. field below is its). ⚠ A lookup refusal line, if one stands, keeps
   * the field with it — every line points at that field.
   * ⚠ AND ON OTHER, his words of 2026-10-26: *"Hide the Fr field on Other
   * notes."* — the lookup line keeping it there too, his answer the same
   * night: *"Agreed: do the same on Other."* */
  const frField=((isObs(n)||isOther(n))&&!lkText)?"":'<div class="field"><label>Fr. number <span style="text-transform:none;letter-spacing:0;color:var(--muted);font-weight:400">(optional)</span></label><input id="cfr" placeholder="e.g. 1802-1" value="'+escH(n.fr||"")+'" maxlength="18">'+frHint+'</div>';
  /* ⚠ OTHER'S STEP 5, his words: *"On all other notes grade tiles should list VG8, F12, V20, EF40, CU60, Ch63, Gem65
   * with no value listed on the tiles, the collector inputs that themselves."* — "other notes" the Other type, "V20"
   * VF20, both his confirmation; the seven drawn from `GRADES` itself, his go, never the entry's own list. */
  const gtabs=(isOther(n)?GRADES:gradesOf(n)).map((g,i)=>{const p=n.g[i];
    return `<button class="grtab ${h.grade===g?"on":""}" ${(p==null&&!nopx)?"disabled":""} data-setgrade="${g}">${GLABEL[g]||g}${aiG&&h.grade===g?star:""}${isOther(n)?"":`<span class="pp">${money(p)}</span>`}</button>`;}).join("")
    /* The custom tab — Phase 6.5 step 3.3, his ruling 3. On every note, after
     * the price tabs, never disabled: it is the one grade with no price, so the
     * "disabled when unpriced" rule above must not reach it. Concatenated, not
     * a template literal, so the twin's anchors carry no `${` and anchor-sweep
     * can check them — the same reason editor.js gives for its log line. */
    /* His ruling 2026-09-16 (second walk): the chip reads "Custom" over
     * "Grade", two lines in the SAME type — not the small grey `.pp`
     * sub-label the price tabs carry. `.ln` breaks the line and inherits
     * everything else from the chip. */
    +'<button class="grtab '+(isCustomGrade(h.grade)?"on":"")+'" data-setgrade="'+CUSTOM_GRADE+'">'+(GLABEL[CUSTOM_GRADE]||CUSTOM_GRADE)+(aiG&&isCustomGrade(h.grade)?star:"")+'<span class="ln">Grade</span></button>'
    /* The chip expands into a number field when tapped — his ruling
     * 2026-09-17. The number is the grade key's (catalog.js); it prices
     * nothing. Rendered only while the chip is on, beside it in the row. */
    +(isCustomGrade(h.grade)?'<input class="cgn" id="cgn" inputmode="numeric" maxlength="2" placeholder="grade" aria-label="Custom grade number" value="'+escH(customGradeNumber(h.grade)||"")+'">':'');
  /* The EPQ/PPQ switch — Phase 6.5 step 3.5. His ruling 3 (2026-09-13): "in
   * the grade section, off by default, affecting no value"; ruling 2
   * (2026-09-15): it lives in memo and is a READ of the text; 2026-09-17:
   * "the same control as the public/private toggle, in its current place".
   * So there is no flag: it is on exactly when the word is in the
   * collector's notes, and the label says which word. Concatenated, like
   * the custom tab, so the twin's anchors carry no interpolation. */
  /* His ruling 2026-09-21: a total that drops still needs a reason, and
   * the two — now three — kinds of "no price" must not read the same.
   * ⚠ The test is the catalogue's own `st`, never a guess. */
  const priceNote=()=>{
    if(pricedAt(n,h.grade))return "";
    if(n.st==="Current")return "In circulation \u2014 no collector price";
    if(n.st)return "No catalog price \u2014 "+n.st;
    /* a blank grade has no "this grade" — Other's step 5, the wording his go approved */
    if(!h.grade)return "No catalog price \u2014 add your own value.";
    return "No catalog price for this grade \u2014 add your own value.";
  };
  const why=priceNote();
  const whyRow=why?'<div class="whyrow">'+why+'</div>':"";
  /* ⚠⚠ THE SUB-LABEL CHANGED WITH THE COLUMN — 2026-09-21. It read
   * ~~`EPQ · in your notes`~~, which told the collector WHERE the state
   * lived, and after db/012 that sentence is simply false: the state is
   * a column and the memo carries no letters. It now reads `on` / `off`,
   * and it says no WORD because **EPQ and PPQ are indistinguishable on
   * purpose — his ruling 3, 2026-09-21: on means "EPQ/PPQ".** A label
   * printing one of the two words would be claiming a distinction the
   * stored boolean cannot make. */
  const on=qualifierOn(h);
  const qual='<div class="qualrow"><span class="lab">EPQ / PPQ'+(aiQ?star:"")+'<span class="pp">'+(on?"on":"off")+'</span></span><button class="toggle '+(on?"on":"")+'" data-setqual="'+h.id+'" aria-label="EPQ or PPQ"><span class="knob"></span></button></div>';
  const e=estVal(h);const gl=e!=null?e-(+h.cost||0):null;const glc=gl==null?"":gl>=0?"pos":"neg";
  const pct=(gl!=null&&+h.cost)?((gl/+h.cost)*100):null;
  /* ── HIS WALK OF STEP 6, 2026-10-04, AND HIS RULING 1 (rule (a)) — on EVERY
   * custom note the photos come first: the frames, his line while the front
   * frame is empty (`h.hasFront` is set by the local write, `putPhoto`, so
   * the next render after "Use photo" drops it), the reading card, then Note
   * details. A catalogue note's order is unchanged (`rdBox` is always empty
   * there), and it never shows the line (pending.js says why). */
  /* ── THE "OTHER" TYPE, his rulings of 2026-10-26. The Type list ends with
   * it — a type no catalogue row carries, so `sectionList()` never names it.
   * His ruling 1: *"Ask for the value on every Other note and show 'add your
   * value' on the card until there is one. Don't block saving."* — so the
   * field drops "(optional)" and says why, and nothing refuses a save. The
   * hint's words are the builder's; his answer 6: *"Use your wording for
   * now; I'll judge it on screen."* Concatenated, not template literals, so
   * the twin's anchors carry no interpolation. */
  const typeList=["Custom entry"].concat(sectionList(),[OTHER_TYPE]);
  const apprOpt=isOther(n)?"":' <span style="text-transform:none;letter-spacing:0;color:#5f7a68;font-weight:400">(optional)</span>';
  const apprHint=isOther(n)?OTHER_VALUE_HINT:(bookVal(h)!=null?"Overrides the catalog value for this note.":"This note has no catalog price — enter your own valuation.");
  const firstLine=isCust(n)&&!h.hasFront?'<div class="fieldhint photofirst">'+PHOTO_FIRST_LINE+'</div>':"";
  const photos=`<div class="field"><label>Photos of your note</label>
      <div class="photobox"><div class="frames">
        <div class="pframe" id="pf_front" data-photoadd="${h.id}:front"><div class="ph"><span class="cam">⧉</span>Add front</div><span class="tag">Front</span></div>
        <div class="pframe" id="pf_back" data-photoadd="${h.id}:back"><div class="ph"><span class="cam">⧉</span>Add back</div><span class="tag">Back</span></div>
      </div></div>${firstLine}
    </div>`;
  return `<button class="back" data-back="collection">‹ Back to vault</button>
    <div class="hero"><div class="fr">${escH(isCust(n)?(n.fr?"Fr. "+n.fr:"New note"):(isErr(n)||isCol(n)||isObs(n)?n.fr:"Fr. "+n.fr))}${idStar}</div><div class="dn">${escH(isCust(n)?((n.denom?n.denom+" · ":"")+typeLabel(n.section)):(isErr(n)?typeLabel(n.section):(isCol(n)?n.denom+" · Colonial":n.denom+" · "+typeLabel(n.section))))}</div>${n.desc?`<div class="ds">${escH(n.desc)}</div>`:""}${detailLine(n)?`<div class="ds nline">${detailLine(n)}</div>`:""}</div>
    ${notRight}${difBox}${uncBox}${isCust(n)?photos+rdBox:rdBox+photos}
    ${isCust(n)?`<div class="acard" style="margin-bottom:16px"><div class="acard-h">Note details</div>
      ${frField}
      <div class="field"><label>Denomination${aiD?star:""}</label><input id="cdenom" placeholder="e.g. $20" value="${escH(n.denom||"")}" maxlength="14"></div>
      <div class="field"${stF?"":' style="margin-bottom:0"'}><label>Type${aiT?star:""}</label><select id="csection">${aiT&&isOther(n)?`<option value="${OTHER_LEAD}" selected>${CARD.otherFilled}</option>`:""}${typeList.map(x=>`<option value="${x}" ${n.section===x&&!(aiT&&isOther(n))?"selected":""}>${typeLabel(x)}</option>`).join("")}</select></div>
      ${stF?'<div class="field" style="margin-bottom:0"><label>'+CARD.labels.state+(aiS?star:"")+'</label><input id="cstate" placeholder="'+CARD.statePlaceholder+'" value="'+escH(n.desc||"")+'" maxlength="40"></div>':""}
    </div>`:""}
    ${isObs(n)?`<div class="field"><label>Reference no.</label><input id="ref" placeholder="e.g. Haxby VA-25-G4" value="${escH(h.ref||"")}" maxlength="40"><div class="fieldhint">Obsolete notes have no Fr. number — record your own reference (Haxby number, issuing bank, or your own system).</div></div>`:""}<div class="field"><label>Grade</label><div class="grtabs">${gtabs}</div>${whyRow}${qual}</div>
    <div class="field"><label>Series year on the note${aiY?star:""}</label><input id="yr" inputmode="numeric" placeholder="e.g. 1923" value="${escH(h.year||"")}" maxlength="4"><div class="fieldhint">Auto-filled from the catalog when known — edit if it's off.</div></div>
    <div class="field"><label>What you paid (cost basis)</label><input id="cost" inputmode="numeric" placeholder="0" value="${escH(h.cost||"")}"></div>
    <div class="field"><label>Your appraised value${apprOpt}</label><input id="appr" inputmode="numeric" placeholder="${bookVal(h)!=null?money(bookVal(h)).slice(1)+" (catalog)":"no catalog price"}" value="${escH(h.appr||"")}"><div class="fieldhint">${apprHint}${h.appr?' <b style="color:var(--gold)">Using your value.</b>':""}</div></div>
    <div class="field"><label>Acquired</label><input id="acq" type="date" value="${escH(h.acq||"")}"></div>
    <div class="field"><label>Serial / notes (optional)</label><input id="memo" placeholder="e.g. serial A01944756A" value="${escH(h.memo||"")}"></div>
    <div class="calc">
      <div class="r"><span class="lab">${h.appr?"Your appraised value":"Catalog value"+(h.grade?" ("+gradeLabel(h.grade)+")":"")}</span><span class="mono">${money(e)}</span></div>
      ${h.appr&&bookVal(h)!=null?`<div class="r"><span class="lab">Catalog (${GLABEL[h.grade]||h.grade})</span><span class="mono" style="color:#5f7a68">${money(bookVal(h))}</span></div>`:""}
      <div class="r"><span class="lab">Cost basis</span><span class="mono">${money(+h.cost||0)}</span></div>
      <div class="r tot"><span class="lab">Gain / loss</span><span class="val mono ${glc}">${gl==null?"—":(gl>=0?"+":"−")+money(Math.abs(gl)).slice(1)}${pct!=null?`  (${pct>=0?"+":""}${pct.toFixed(1)}%)`:""}</span></div>
    </div>
    <button class="btn" data-savenote="${h.id}">Save note to collection</button>
    <button class="btn ghost" data-remove="${h.id}" style="color:var(--loss)">Remove from vault</button>
    ${state.vaultZoom?vVaultZoom(state.vaultZoom):""}`;
}

/* ══ ⚠⚠⚠ THE VAULT ENLARGE — THE OWNER'S SIDE OF THE 2026-08-04 REGISTER
 * ITEM *"tapping a photo must OPEN it, not replace it"* ══════════════
 *
 * Decided by Jesse 2026-09-03, and the decision is recorded in
 * BUILD_NOTES before this function was written: tile tap enlarges on BOTH
 * surfaces, and the Replace chip becomes the ONLY replace target.
 *
 * ⚠⚠ THE POINT OF THE ITEM IS THE MIS-TAP DIRECTION, NOT THE VIEWER.
 * Until today the whole filled tile was a replace target, so a stray
 * finger was one step from overwriting an irreplaceable photograph. It
 * now lands on the viewer. **The safety property is what the tile does
 * when you MISS**, and that is why `data-photoadd` had to come OFF the
 * frame rather than merely have a viewer added beside it.
 *
 * ⚠⚠⚠ NO CONTROLS IN HERE, AND THAT IS JESSE'S INSTRUCTION RATHER THAN
 * an inherited default: *"strictly the photograph, so a collector sees
 * the note in full detail."* He rejected putting Replace in the bar. So
 * this viewer holds a side label, a close control, and a failure line —
 * the same subtracted discipline as `vPublicZoom`, arrived at for the
 * owner's screen for a different reason: on a stranger's note the
 * subtraction protects THEIR data, here it protects the photograph from
 * its owner's own thumb.
 *
 * ⚠ SEPARATE FROM `vPublicZoom` ON PURPOSE — proposed by the builder,
 * ruled on by Jesse. The two render nearly the same markup today. The
 * Community viewer's emptiness is a SAFETY property (a control added
 * there reaches a stranger's screen), and sharing the function is how a
 * vault control would one day arrive there by an edit nobody reviewed as
 * a Community change. `.pvw` — the CSS — is shared, because a stylesheet
 * rule cannot carry an affordance.
 *
 * ⚠ THE SLOT COMES FROM `state.vaultZoom`, NOT FROM THE TAP, for the
 * reason `vPublicZoom` records: `render()` rebuilds this screen's
 * innerHTML on every pass, so a viewer held in the DOM alone would vanish
 * on the next one. */
/* ⚠ THE NOTE PANE BEFORE A NOTE IS CHOSEN — B only (render.js `renderPanes`), his sentence exactly (ruling 8,
 * 2026-10-24). Not the first note opened on its own: opening one runs a note's screen — the card mark's test, the
 * upload toasts — for a note nobody chose. */
const PICK_LINE="Choose a note to see it here.";
function vPick(){return '<div class="empty pick"><div class="ic"><span>◈</span></div><p>'+PICK_LINE+'</p></div>';}

function vVaultZoom(slot){
  const side=slot==="front"?"Front":"Back";
  return `<div class="pvw" id="vpz">
    <div class="pvw-bar">
      <span class="pvw-side">${side}</span>
      <button class="pvw-x" data-vpzclose aria-label="Close">✕</button>
    </div>
    <div class="pvw-stage"><img id="vpz_img" alt="${side} of your note, enlarged"></div>
    <div class="pvw-foot"><div id="vpznotice"></div></div>
  </div>`;
}

export {
  PICK_LINE,
  vCatalog,
  vCollection,
  vDetail,
  vHolding,
  vPick,
}
